import { Box, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

/**
 * One tab in a section's navigation row.
 *
 * `to` is the react-router target (relative to the shell's route, e.g.
 * `"leads"`, or `""` for the index tab). `end` forces an exact match —
 * pass it for the index tab so it isn't marked active on every child URL.
 */
export interface SectionTab {
  to: string;
  label: string;
  end?: boolean;
  /**
   * Tiny pill after the label — "NEW", "BETA", a count. Optional.
   *
   * Anything set here should be TEMPORARY: a badge that never comes off
   * stops being information and becomes decoration everyone learns to
   * ignore. Callers are expected to gate it on an expiry (see
   * `MarketingLayout`'s `NEW_BADGE_UNTIL`).
   */
  badge?: string;
}

/**
 * Section tab — **the one canonical definition**. A pill on the tinted menu
 * strip, matching the app-wide segmented-tab look (the theme's `MuiTabs`
 * override + the CRM `DetailTabs`): idle = muted text, no fill; hover = text
 * lift on a faint paper wash; active (NavLink's auto `.active` class) = a
 * paper pill with a hairline + the quiet card shadow and brand text. The
 * strip itself is the track, so the pill needs no extra container.
 * `styled(NavLink)` keeps react-router's routing props and the auto active
 * class.
 *
 * This used to be an underline (`::after` bar) copy-pasted (with drift) into
 * every shell — SalesLayout / SupportLayout / MarketingLayout / AdminLayout
 * each carried their own `TabLink`. It now lives here once.
 */
const TabLink = styled(NavLink)(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  flexShrink: 0,
  alignItems: "center",
  minHeight: 32,
  padding: "6px 14px",
  borderRadius: theme.shape.borderRadius,
  border: "1px solid transparent",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.2,
  textDecoration: "none",
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(["color", "background-color", "box-shadow"], {
    duration: 150,
  }),
  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
  },
  "&.active": {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    boxShadow: theme.shadows[1],
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
}));

/**
 * The little pill a tab can carry after its label ("NEW", "BETA").
 *
 * Brand lime on navy text: it reads as a flag rather than as a status
 * colour, and it stays legible whether the tab is idle (muted grey) or
 * active (brand navy), so it never competes with the active underline.
 */
const TabBadge = styled("span")(({ theme }) => ({
  marginLeft: 6,
  padding: "1px 5px",
  borderRadius: 4,
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1.5,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.primary.main,
}));

/**
 * The shared **section shell header** for every top-level dashboard
 * section — Sales, Customer Support, Marketing, System Admin, Server
 * Status, Tester. A single full-bleed bar, flush under the main top nav
 * and spanning the viewport, that carries the section title, an optional
 * right-hand control cluster, and (for tabbed shells) the segmented (pill) tab
 * navigation. It bleeds past `<main>`'s padding (`px {xs:3, lg:4}`, `py 3`)
 * with matching negative margins; its own inner padding re-aligns the
 * title + tabs with the page content below.
 *
 * **`tabs` is optional.** Omit it and you get a title-only bar — what the
 * single-page sections (Server Status, Tester) use. Pass it and the nav
 * row renders below the title.
 *
 * **`<SectionHeader>` vs `<PageHeader>`.** This is the *shell* header that
 * owns the section title + tab navigation. `<PageHeader>` is the lighter
 * in-page title block used by sub-pages that render inside a shell's
 * `<Outlet>` (a ticket detail, a doc page, an admin panel's own title).
 * Don't reach for `<SectionHeader>` inside a sub-page.
 *
 * **CRM is the exception.** CRM (`apps/web/src/crm/`) keeps its own denser
 * `components/ui/page-header.tsx` — don't import this one there, and don't
 * import the CRM one here. The Dashboard pulse hero (`PulseHeader`) and the
 * centered Profile page are likewise purpose-built and don't use this bar.
 *
 * Sub-pages can still inject a primary action into the right cluster via
 * the `<HeaderAction>` portal — the shell mounts the portal target inside
 * whatever it passes to `right`.
 *
 * `tabsRight` is a second, *menu-row* slot: status-y content that belongs
 * with the tabs rather than with the title controls, pinned to the far
 * right of the tinted nav strip (Sales puts its two agent pills there).
 * Only the TABS scroll when the window is narrow — the `tabsRight` cluster
 * is outside that scroll region, so it can never slide out of sight.
 */
export function SectionHeader({
  title,
  subtitle,
  tabs,
  tabsAriaLabel,
  right,
  tabsRight,
}: {
  title: string;
  subtitle?: string;
  tabs?: readonly SectionTab[];
  /** Accessible label for the tab `<nav>`; defaults to `"<title> sections"`. */
  tabsAriaLabel?: string;
  right?: ReactNode;
  /** Rendered at the far right of the tab (menu) row. */
  tabsRight?: ReactNode;
}): JSX.Element {
  return (
    <Box
      component="header"
      data-testid="section-header"
      sx={{
        // Bleed flush under the top nav and out to the viewport edges, then
        // re-pad so the title + tabs line up with the page content below.
        // The header itself stays on the page (NO fill) — the title + controls
        // sit on white. Only the tab "menu row" below gets a tinted strip
        // (see the <nav> sx). The bottom hairline closes the section header
        // (and is the only edge a title-only page needs).
        mt: -3,
        mx: { xs: -3, lg: -4 },
        mb: { xs: 2, lg: 2.5 },
        px: { xs: 3, lg: 4 },
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 48,
          py: 0.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              component="p"
              sx={{ mt: 0.25, maxWidth: "60rem", fontSize: 13, lineHeight: 1.35, color: "text.secondary" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {right && (
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
            {right}
          </Box>
        )}
      </Box>

      {tabs && tabs.length > 0 && (
        <Box
          component="nav"
          aria-label={tabsAriaLabel ?? `${title} sections`}
          data-testid="section-nav"
          sx={{
            // The MENU ROW is its own tinted, full-bleed strip — a clean light
            // neutral wash (NOT the title row, NOT a colour), bounded by a top
            // hairline and the header's bottom hairline, so the tabs read as a
            // distinct menu bar against the white title area + page. Bleeds to
            // the viewport edges (negating the header px) then re-pads the tabs
            // to the content margin.
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 1,
            mx: { xs: -3, lg: -4 },
            px: { xs: 3, lg: 4 },
            py: 0.75,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            borderTop: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {/* The TABS scroll (narrow windows); the `tabsRight` cluster does
              NOT — it stays pinned to the right edge instead of sliding off
              into a scroll region the operator can't see. */}
          <Box
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              gap: 1,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {tabs.map((t) => (
              <TabLink key={t.to} to={t.to} end={t.end ?? false}>
                {t.label}
                {t.badge && <TabBadge>{t.badge}</TabBadge>}
              </TabLink>
            ))}
          </Box>
          {tabsRight && (
            <Box sx={{ pl: 2, flexShrink: 0, display: "flex", alignItems: "center", gap: 2 }}>
              {tabsRight}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
